import React, { useState, useEffect } from "react";
import { updateCategory } from "../../../api/categoryApi";
import { uploadCatalogImage } from "../../../api/productApi"; // <-- for replacing image
import { toast } from "react-toastify";
import PropTypes from "prop-types";

// --- shared helpers (or move to a util and import) ---
const normalizeKey = (s = "") => {
  let v = String(s).trim();
  v = v.replace(/^\/+/, "");
  v = v.replace(/^catelog\//i, "catalog/"); // legacy typo
  if (!/^catalog\//i.test(v)) v = `catalog/${v.split("/").pop()}`;
  return v;
};
const toPublicUrl = (v = "") => {
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v; // already a full URL
  const key = normalizeKey(v);
  const base = (process.env.REACT_APP_API_IMAGE_BASE_URL ||
    "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com").replace(/\/+$/, "");
  return `${base}/${key}`;
};
// -----------------------------------------------------

const EditCategoryList = ({ selectedCategory, refreshList }) => {
  const [nameEN, setNameEN] = useState("");
  const [nameAR, setNameAR] = useState("");
  const [slugEN, setSlugEN] = useState("");
  const [slugAR, setSlugAR] = useState("");
  const [status, setStatus] = useState(true);

  // keep the S3 key we will save to backend
  const [imageKey, setImageKey] = useState("");
  // keep a preview URL (either local dataURL during upload or final S3 URL)
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (!selectedCategory) return;

    const name = selectedCategory.category;
    const slug = selectedCategory.categoryslug;

    setNameEN(typeof name === "object" ? name?.en || "" : name || "");
    setNameAR(typeof name === "object" ? name?.ar || "" : name || "");

    setSlugEN(typeof slug === "object" ? slug?.en || "" : slug || "");
    setSlugAR(typeof slug === "object" ? slug?.ar || "" : slug || "");

    const key = normalizeKey(selectedCategory.image || "");
    setImageKey(key);
    setPreviewUrl(toPublicUrl(key)); // <-- show S3 URL in edit preview

    setStatus(selectedCategory.status === "active");
  }, [selectedCategory]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // instant local preview
    const reader = new FileReader();
    reader.onloadend = async () => {
      const localDataUrl = reader.result;
      setPreviewUrl(localDataUrl);

      try {
        // build deterministic filename like in "add" flow
        const ext =
          (file.type && file.type.split("/")[1]) ||
          file.name.split(".").pop() ||
          "jpg";
        const stamp = new Date().toISOString().replace(/[-:.TZ]/g, "").slice(0, 14);
        const base = (slugEN || nameEN || "category")
          .toString()
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/[^a-z0-9-_]/g, "");
        const desiredName = `CAT_${base || "category"}_${stamp}.${ext}`;

        // upload and switch preview to final S3 URL
        const { key } = await uploadCatalogImage(localDataUrl, desiredName);
        const k = normalizeKey(key);
        setImageKey(k);
        setPreviewUrl(toPublicUrl(k));
      } catch (err) {
        console.error(err);
        toast.error(window.translate("Image upload failed"));
        // fall back to old image
        setPreviewUrl(toPublicUrl(imageKey));
      }
    };
    reader.readAsDataURL(file);
  };

  const handleUpdate = async () => {
    if (!nameEN || !slugEN || !slugAR) {
      toast.warn("Please fill all required fields.");
      return;
    }

    const payload = {
      name: { en: nameEN, ar: nameAR },
      slug: { en: slugEN, ar: slugAR },
      status: status ? "active" : "inactive",
      image: imageKey, // <-- save key, not dataURL
    };

    try {
      await updateCategory(selectedCategory._id, payload);
      toast.success(`${window.translate("Category updated successfully!")}`);
      refreshList();
      setTimeout(() => window.location.reload(), 800);

      // close modal
      const modal = document.getElementById("edit-category");
      modal?.classList.remove("show");
      document.querySelector(".modal-backdrop")?.remove();
      document.body.classList.remove("modal-open");
    } catch (err) {
      console.error("Update error:", err);
      toast.error(
        err?.response?.data?.message ||
          `${window.translate("Failed to update category")}`
      );
    }
  };

  return (
    <div className="modal fade" id="edit-category" tabIndex="-1" key={selectedCategory?._id || "no-category"}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="page-wrapper-new p-0">
            <div className="content">
              <div className="modal-header">
                <div className="page-title">
                  <h4>{window.translate("Edit Category")}</h4>
                </div>
                <button type="button" className="close bg-danger text-white fs-16" data-bs-dismiss="modal" aria-label="Close">
                  <span aria-hidden="true">×</span>
                </button>
              </div>

              <div className="modal-body">
                <form>
                  <div className="mb-3">
                    <label className="form-label">
                      {window.translate("Category Image")} <span className="text-danger ms-1">*</span>
                    </label>
                    <input type="file" accept="image/*" className="form-control" onChange={handleFileChange} />
                    {previewUrl && (
                      <img
                        src={previewUrl}
                        alt="Preview"
                        style={{ width: "80px", height: "80px", marginTop: "10px", objectFit: "cover" }}
                      />
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      {window.translate("Category")} (EN)<span className="text-danger ms-1">*</span>
                    </label>
                    <input type="text" className="form-control" value={nameEN} onChange={(e) => setNameEN(e.target.value)} />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{window.translate("Category")} (AR)</label>
                    <input type="text" className="form-control" value={nameAR} onChange={(e) => setNameAR(e.target.value)} />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      {window.translate("Slug (EN)")} <span className="text-danger ms-1">*</span>
                    </label>
                    <input type="text" className="form-control" value={slugEN} onChange={(e) => setSlugEN(e.target.value)} />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      {window.translate("Slug (AR)")} <span className="text-danger ms-1">*</span>
                    </label>
                    <input type="text" className="form-control" value={slugAR} onChange={(e) => setSlugAR(e.target.value)} />
                  </div>

                  <div className="mb-0">
                    <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                      <span className="status-label">
                        {window.translate("Status")} <span className="text-danger ms-1">*</span>
                      </span>
                      <input
                        type="checkbox"
                        id="status-toggle"
                        className="check"
                        checked={status}
                        onChange={(e) => setStatus(e.target.checked)}
                      />
                      <label htmlFor="status-toggle" className="checktoggle" />
                    </div>
                  </div>
                </form>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none" data-bs-dismiss="modal">
                  {window.translate("Cancel")}
                </button>
                <button type="button" className="btn btn-primary fs-13 fw-medium p-2 px-3" onClick={handleUpdate}>
                  {window.translate("Save Changes")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

EditCategoryList.propTypes = {
  selectedCategory: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    category: PropTypes.oneOfType([
      PropTypes.shape({ en: PropTypes.string, ar: PropTypes.string }),
      PropTypes.string,
    ]),
    categoryslug: PropTypes.oneOfType([
      PropTypes.shape({ en: PropTypes.string, ar: PropTypes.string }),
      PropTypes.string,
    ]),
    status: PropTypes.string,
    image: PropTypes.string,
  }),
  refreshList: PropTypes.func.isRequired,
};

export default EditCategoryList;
